/**
 * Strategy Alignment Service
 * 
 * Makes C3.A3 (Leaders audit alignment by inspecting mappings) true.
 * 
 * Evidence criteria:
 * - Strategic objectives are explicitly defined
 * - Priorities can be mapped to objectives
 * - Leaders can inspect alignment via observable artifacts
 * - Alignment gaps are detectable
 */

import { getSupabaseClient } from './supabaseClient';
import { getCurrentWorkspaceId } from './supabaseSync';

// ============================================================================
// Types
// ============================================================================

/** Strategy is the parent container / outcome node (top of OST). */
export interface Strategy {
  id: string;
  name: string;
  description?: string;
  /** Cycles that list this strategy as theirs from the strategy's side (`linked_cycle_ids`); see strategyIdForCycle. */
  linkedCycleIds?: string[];
  workspaceId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Goal = metric. Sits below an Objective in the hierarchy: Strategy → Objective → Goal.
 * A Goal is the measurable signal that tells you the Objective is being achieved.
 * It is not a qualitative statement — it has a metric name, target value, unit, and
 * optionally a current value. A single Objective can have multiple Goals.
 */
export interface Goal {
  id: string;
  name: string;
  description: string;
  timeHorizon: 'quarterly' | 'annual' | 'multi-year';
  priority: 'critical' | 'high' | 'medium' | 'low';
  status: 'active' | 'achieved' | 'deferred' | 'cancelled';
  /** What is being measured (e.g. "Monthly active users"). */
  metricName: string | null;
  /** The numeric or string target (e.g. "10000"). */
  targetValue: string | null;
  /** Current value at last update (e.g. "6200"). */
  currentValue: string | null;
  /** Unit of measurement (e.g. "users", "%", "$K"). */
  unit: string | null;
  /** Parent objective (primary link). */
  objectiveId?: string;
  /** Convenience: strategy derived from the parent objective. */
  strategyId?: string;
  workspaceId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface StrategicObjective {
  id: string;
  name: string;
  description: string;
  timeHorizon: 'quarterly' | 'annual' | 'multi-year';
  priority: 'critical' | 'high' | 'medium' | 'low';
  status: 'active' | 'achieved' | 'deferred' | 'cancelled';
  /** Parent strategy this objective belongs to. */
  strategyId?: string;
  /** Parent goal this objective belongs to (more specific than strategy). */
  goalId?: string;
  parentObjectiveId?: string;
  /**
   * Share of its strategy's 100 points (migration 866). 0 across a strategy
   * means not weighted yet. Read-only here: written for the whole strategy at
   * once by setObjectiveWeights.
   */
  weight: number;
  workspaceId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ObjectivePriorityMapping {
  id: string;
  objectiveId: string;
  priorityId: string; // ID from focus list items
  alignmentStrength: 'primary' | 'secondary' | 'indirect';
  /** A goal under this objective that the item moves (migration 924). Absent = the objective as a whole. */
  goalId?: string;
  rationale?: string;
  createdBy: string;
  createdAt: string;
}

export interface AlignmentGap {
  type: 'unmapped_priority' | 'unsupported_objective' | 'weak_alignment';
  description: string;
  entityId: string;
  entityName: string;
  severity: 'high' | 'medium' | 'low';
  recommendation: string;
}

export interface AlignmentReport {
  generatedAt: string;
  workspaceId: string;
  totalObjectives: number;
  mappedObjectives: number;
  totalPriorities: number;
  mappedPriorities: number;
  alignmentScore: number; // 0-100
  gaps: AlignmentGap[];
  mappings: ObjectivePriorityMapping[];
  objectives: StrategicObjective[];
}

// ============================================================================
// DB row shape (snake_case) -> domain type mappers
// ============================================================================

function strategyFromRow(r: {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  linked_cycle_ids?: string[] | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}): Strategy {
  return {
    id: r.id,
    name: r.name,
    description: r.description ?? undefined,
    linkedCycleIds: r.linked_cycle_ids ?? [],
    workspaceId: r.workspace_id,
    createdBy: r.created_by,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

function goalFromRow(r: {
  id: string;
  workspace_id: string;
  strategy_id: string | null;
  objective_id?: string | null;
  name: string;
  description: string;
  time_horizon: string;
  priority: string;
  status: string;
  metric_name?: string | null;
  target_value?: string | null;
  current_value?: string | null;
  unit?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}): Goal {
  return {
    id: r.id,
    name: r.name,
    description: r.description,
    timeHorizon: r.time_horizon as Goal['timeHorizon'],
    priority: r.priority as Goal['priority'],
    status: r.status as Goal['status'],
    metricName: r.metric_name ?? null,
    targetValue: r.target_value ?? null,
    currentValue: r.current_value ?? null,
    unit: r.unit ?? null,
    objectiveId: r.objective_id ?? undefined,
    strategyId: r.strategy_id ?? undefined,
    workspaceId: r.workspace_id,
    createdBy: r.created_by,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

function objectiveFromRow(r: {
  id: string;
  workspace_id: string;
  strategy_id: string | null;
  goal_id?: string | null;
  parent_objective_id: string | null;
  name: string;
  description: string;
  time_horizon: string;
  priority: string;
  status: string;
  weight?: number | string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}): StrategicObjective {
  return {
    id: r.id,
    name: r.name,
    description: r.description,
    timeHorizon: r.time_horizon as StrategicObjective['timeHorizon'],
    priority: r.priority as StrategicObjective['priority'],
    status: r.status as StrategicObjective['status'],
    strategyId: r.strategy_id ?? undefined,
    goalId: r.goal_id ?? undefined,
    parentObjectiveId: r.parent_objective_id ?? undefined,
    weight: Number(r.weight ?? 0),
    workspaceId: r.workspace_id,
    createdBy: r.created_by,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

function mappingFromRow(r: {
  id: string;
  objective_id: string;
  priority_id: string;
  alignment_strength: string;
  goal_id?: string | null;
  rationale: string | null;
  created_by: string;
  created_at: string;
}): ObjectivePriorityMapping {
  return {
    id: r.id,
    objectiveId: r.objective_id,
    priorityId: r.priority_id,
    alignmentStrength: r.alignment_strength as ObjectivePriorityMapping['alignmentStrength'],
    goalId: r.goal_id ?? undefined,
    rationale: r.rationale ?? undefined,
    createdBy: r.created_by,
    createdAt: r.created_at,
  };
}

// ============================================================================
// Strategy CRUD (Supabase as system of record per build contract)
// ============================================================================

// createStrategy lived here as a second writer into `strategies`; the one
// path is now strategyStore.createStrategy (round 8, area 3).

export async function getStrategy(id: string): Promise<Strategy | null> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.from('strategies').select('*').eq('id', id).maybeSingle();
  if (error) {
    console.error('[StrategyAlignment] Failed to get strategy:', error);
    return null;
  }
  return data ? strategyFromRow(data) : null;
}

export async function listStrategies(workspaceId: string): Promise<Strategy[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('strategies')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('name');
  if (error) {
    console.error('[StrategyAlignment] Failed to list strategies:', error);
    return [];
  }
  return (data ?? []).map(strategyFromRow);
}

export async function updateStrategy(
  id: string,
  updates: Partial<Omit<Strategy, 'id' | 'createdAt' | 'createdBy'>>
): Promise<Strategy | null> {
  const supabase = getSupabaseClient();
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (updates.name !== undefined) row.name = updates.name;
  if (updates.description !== undefined) row.description = updates.description;
  if (updates.workspaceId !== undefined) row.workspace_id = updates.workspaceId;
  const { data, error } = await supabase.from('strategies').update(row).eq('id', id).select().maybeSingle();
  if (error) {
    console.error('[StrategyAlignment] Failed to update strategy:', error);
    return null;
  }
  return data ? strategyFromRow(data) : null;
}

export async function deleteStrategy(id: string): Promise<boolean> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from('strategies').delete().eq('id', id);
  if (error) {
    console.error('[StrategyAlignment] Failed to delete strategy:', error);
    return false;
  }
  return true;
}

// ============================================================================
// Goals CRUD  (Strategy → Objective → Goal)
// ============================================================================

export async function createGoal(
  input: Omit<Goal, 'id' | 'createdAt' | 'updatedAt'>,
  userId: string
): Promise<Goal> {
  const supabase = getSupabaseClient();
  const workspaceId = input.workspaceId || getCurrentWorkspaceId();
  if (!workspaceId) throw new Error('Workspace required to create goal.');
  const id = crypto.randomUUID();
  const now = new Date().toISOString();
  const row = {
    id,
    workspace_id: workspaceId,
    objective_id: input.objectiveId ?? null,
    strategy_id: input.strategyId ?? null,
    name: input.name,
    description: input.description ?? '',
    time_horizon: input.timeHorizon,
    priority: input.priority,
    status: input.status,
    metric_name: input.metricName ?? null,
    target_value: input.targetValue ?? null,
    current_value: input.currentValue ?? null,
    unit: input.unit ?? null,
    created_by: userId,
    created_at: now,
    updated_at: now,
  };
  const { data, error } = await supabase.from('goals').insert(row).select().single();
  if (error) {
    console.error('[StrategyAlignment] Failed to insert goal:', error);
    throw new Error(error.message || 'Failed to save goal');
  }
  return goalFromRow(data);
}

export async function listGoals(
  workspaceId: string,
  /** Filter by parent objective. */
  objectiveId?: string,
  /** Filter by strategy (convenience — matches goals whose parent objective belongs to this strategy). */
  strategyId?: string,
): Promise<Goal[]> {
  const supabase = getSupabaseClient();
  let q = supabase.from('goals').select('*').eq('workspace_id', workspaceId);
  if (objectiveId) q = q.eq('objective_id', objectiveId);
  if (strategyId) q = q.eq('strategy_id', strategyId);
  const { data, error } = await q.order('priority').order('name');
  if (error) {
    console.error('[StrategyAlignment] Failed to list goals:', error);
    return [];
  }
  return (data ?? []).map(goalFromRow);
}

export async function updateGoal(
  id: string,
  updates: Partial<Omit<Goal, 'id' | 'createdAt' | 'createdBy'>>
): Promise<Goal | null> {
  const supabase = getSupabaseClient();
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (updates.name !== undefined) row.name = updates.name;
  if (updates.description !== undefined) row.description = updates.description;
  if (updates.timeHorizon !== undefined) row.time_horizon = updates.timeHorizon;
  if (updates.priority !== undefined) row.priority = updates.priority;
  if (updates.status !== undefined) row.status = updates.status;
  if (updates.objectiveId !== undefined) row.objective_id = updates.objectiveId ?? null;
  if (updates.strategyId !== undefined) row.strategy_id = updates.strategyId ?? null;
  if (updates.metricName !== undefined) row.metric_name = updates.metricName ?? null;
  if (updates.targetValue !== undefined) row.target_value = updates.targetValue ?? null;
  if (updates.currentValue !== undefined) row.current_value = updates.currentValue ?? null;
  if (updates.unit !== undefined) row.unit = updates.unit ?? null;
  const { data, error } = await supabase.from('goals').update(row).eq('id', id).select().maybeSingle();
  if (error) { console.error('[StrategyAlignment] Failed to update goal:', error); return null; }
  return data ? goalFromRow(data) : null;
}

export async function deleteGoal(id: string): Promise<boolean> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from('goals').delete().eq('id', id);
  if (error) { console.error('[StrategyAlignment] Failed to delete goal:', error); return false; }
  return true;
}

// ============================================================================
// Strategic Objectives CRUD (Supabase)
// ============================================================================

export async function createObjective(
  input: Omit<StrategicObjective, 'id' | 'createdAt' | 'updatedAt' | 'weight'>,
  userId: string
): Promise<StrategicObjective> {
  const supabase = getSupabaseClient();
  const workspaceId = input.workspaceId || getCurrentWorkspaceId();
  if (!workspaceId) {
    throw new Error('Workspace required to create objective. Select a workspace and try again.');
  }
  const id = crypto.randomUUID();
  const now = new Date().toISOString();
  const row = {
    id,
    workspace_id: workspaceId,
    strategy_id: input.strategyId ?? null,
    goal_id: input.goalId ?? null,
    parent_objective_id: input.parentObjectiveId ?? null,
    name: input.name,
    description: input.description ?? '',
    time_horizon: input.timeHorizon,
    priority: input.priority,
    status: input.status,
    created_by: userId,
    created_at: now,
    updated_at: now,
  };
  const { data, error } = await supabase.from('strategic_objectives').insert(row).select().single();
  if (error) {
    console.error('[StrategyAlignment] Failed to insert objective:', error);
    throw new Error(error.message || 'Failed to save objective');
  }
  console.log('[StrategyAlignment] Created objective:', input.name);
  return objectiveFromRow(data);
}

export async function getObjective(id: string): Promise<StrategicObjective | null> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.from('strategic_objectives').select('*').eq('id', id).maybeSingle();
  if (error) {
    console.error('[StrategyAlignment] Failed to get objective:', error);
    return null;
  }
  return data ? objectiveFromRow(data) : null;
}

export async function listObjectives(
  workspaceId: string,
  strategyId?: string
): Promise<StrategicObjective[]> {
  const supabase = getSupabaseClient();
  let q = supabase.from('strategic_objectives').select('*').eq('workspace_id', workspaceId);
  if (strategyId != null) {
    q = q.eq('strategy_id', strategyId);
  }
  const { data, error } = await q.order('priority').order('name');
  if (error) {
    console.error('[StrategyAlignment] Failed to list objectives:', error);
    return [];
  }
  const list = (data ?? []).map(objectiveFromRow);
  return list.sort((a, b) => {
    const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });
}

export async function updateObjective(
  id: string,
  updates: Partial<Omit<StrategicObjective, 'id' | 'createdAt' | 'createdBy' | 'weight'>>
): Promise<StrategicObjective | null> {
  const supabase = getSupabaseClient();
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (updates.name !== undefined) row.name = updates.name;
  if (updates.description !== undefined) row.description = updates.description;
  if (updates.timeHorizon !== undefined) row.time_horizon = updates.timeHorizon;
  if (updates.priority !== undefined) row.priority = updates.priority;
  if (updates.status !== undefined) row.status = updates.status;
  if (updates.strategyId !== undefined) row.strategy_id = updates.strategyId ?? null;
  if (updates.goalId !== undefined) row.goal_id = updates.goalId ?? null;
  if (updates.parentObjectiveId !== undefined) row.parent_objective_id = updates.parentObjectiveId ?? null;
  if (updates.workspaceId !== undefined) row.workspace_id = updates.workspaceId;
  const { data, error } = await supabase.from('strategic_objectives').update(row).eq('id', id).select().maybeSingle();
  if (error) {
    console.error('[StrategyAlignment] Failed to update objective:', error);
    return null;
  }
  return data ? objectiveFromRow(data) : null;
}

/**
 * Set every objective weight of one strategy at once (roadmap P-9). The
 * database refuses a set that leaves an objective out or does not add up to
 * exactly 100 (all 0 clears it), and records who moved weight and from what.
 */
export async function setObjectiveWeights(
  strategyId: string,
  weights: Record<string, number>,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { data, error } = await getSupabaseClient().rpc('set_objective_weights', {
    p_strategy_id: strategyId,
    p_weights: weights,
  });
  if (error) return { ok: false, error: error.message };
  const result = data as { success?: boolean; error?: string } | null;
  if (!result?.success) return { ok: false, error: result?.error ?? 'The weights were not saved.' };
  return { ok: true };
}

export async function deleteObjective(id: string): Promise<boolean> {
  const supabase = getSupabaseClient();
  await supabase.from('objective_priority_mappings').delete().eq('objective_id', id);
  const { error } = await supabase.from('strategic_objectives').delete().eq('id', id);
  if (error) {
    console.error('[StrategyAlignment] Failed to delete objective:', error);
    return false;
  }
  return true;
}

// ============================================================================
// Priority Mappings CRUD (Supabase)
// ============================================================================

export async function createMapping(
  input: Omit<ObjectivePriorityMapping, 'id' | 'createdAt'>,
  userId: string
): Promise<ObjectivePriorityMapping> {
  const supabase = getSupabaseClient();
  const id = crypto.randomUUID();
  const now = new Date().toISOString();
  const row = {
    id,
    objective_id: input.objectiveId,
    priority_id: input.priorityId,
    alignment_strength: input.alignmentStrength,
    ...(input.goalId ? { goal_id: input.goalId } : {}),
    rationale: input.rationale ?? null,
    created_by: userId,
    created_at: now,
  };
  const { data, error } = await supabase.from('objective_priority_mappings').insert(row).select().single();
  if (error) {
    console.error('[StrategyAlignment] Failed to insert mapping:', error);
    throw error;
  }
  console.log('[StrategyAlignment] Created mapping:', id);
  return mappingFromRow(data);
}

export async function listMappingsByObjective(objectiveId: string): Promise<ObjectivePriorityMapping[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('objective_priority_mappings')
    .select('*')
    .eq('objective_id', objectiveId);
  if (error) {
    console.error('[StrategyAlignment] Failed to list mappings by objective:', error);
    return [];
  }
  return (data ?? []).map(mappingFromRow);
}

export async function listMappingsByPriority(priorityId: string): Promise<ObjectivePriorityMapping[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('objective_priority_mappings')
    .select('*')
    .eq('priority_id', priorityId);
  if (error) {
    console.error('[StrategyAlignment] Failed to list mappings by priority:', error);
    return [];
  }
  return (data ?? []).map(mappingFromRow);
}

export async function deleteMapping(id: string): Promise<boolean> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from('objective_priority_mappings').delete().eq('id', id);
  if (error) {
    console.error('[StrategyAlignment] Failed to delete mapping:', error);
    return false;
  }
  return true;
}

/** Clears the goal on one link, keeping the objective link (migration 924). */
export async function clearMappingGoal(id: string): Promise<boolean> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from('objective_priority_mappings').update({ goal_id: null }).eq('id', id);
  if (error) {
    console.error('[StrategyAlignment] Failed to clear mapping goal:', error);
    return false;
  }
  return true;
}

/** Fetch all mappings for the given objective ids (for reports and tree). */
async function listMappingsForObjectives(objectiveIds: string[]): Promise<ObjectivePriorityMapping[]> {
  if (objectiveIds.length === 0) return [];
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('objective_priority_mappings')
    .select('*')
    .in('objective_id', objectiveIds);
  if (error) {
    console.error('[StrategyAlignment] Failed to list mappings for objectives:', error);
    return [];
  }
  return (data ?? []).map(mappingFromRow);
}

// ============================================================================
// Alignment Analysis
// ============================================================================

export async function generateAlignmentReport(
  workspaceId: string,
  priorityIds: string[],
  priorityNames: Map<string, string>
): Promise<AlignmentReport> {
  const objectives = await listObjectives(workspaceId);
  const objectiveIds = objectives.map(o => o.id);
  const allMappings = await listMappingsForObjectives(objectiveIds);
  
  // Find priorities that aren't mapped
  const mappedPriorityIds = new Set(allMappings.map(m => m.priorityId));
  const unmappedPriorities = priorityIds.filter(id => !mappedPriorityIds.has(id));
  
  // Find objectives with no priorities
  const objectivesWithMappings = new Set(allMappings.map(m => m.objectiveId));
  const unsupportedObjectives = objectives.filter(o => 
    o.status === 'active' && !objectivesWithMappings.has(o.id)
  );
  
  // Find objectives with only indirect alignment
  const weaklyAlignedObjectives = objectives.filter(o => {
    const objMappings = allMappings.filter(m => m.objectiveId === o.id);
    return objMappings.length > 0 && objMappings.every(m => m.alignmentStrength === 'indirect');
  });
  
  // Build gaps
  const gaps: AlignmentGap[] = [];
  
  for (const priorityId of unmappedPriorities) {
    gaps.push({
      type: 'unmapped_priority',
      description: `Priority "${priorityNames.get(priorityId) || priorityId}" is not mapped to any strategic objective`,
      entityId: priorityId,
      entityName: priorityNames.get(priorityId) || 'Unknown',
      severity: 'medium',
      recommendation: 'Review this priority and map it to a strategic objective, or consider if it should remain in the Item Pool.',
    });
  }
  
  for (const objective of unsupportedObjectives) {
    gaps.push({
      type: 'unsupported_objective',
      description: `Strategic objective "${objective.name}" has no priorities mapped to it`,
      entityId: objective.id,
      entityName: objective.name,
      severity: objective.priority === 'critical' ? 'high' : 'medium',
      recommendation: 'Identify priorities that support this objective or review if additional work is needed.',
    });
  }
  
  for (const objective of weaklyAlignedObjectives) {
    gaps.push({
      type: 'weak_alignment',
      description: `Strategic objective "${objective.name}" only has indirect priority alignment`,
      entityId: objective.id,
      entityName: objective.name,
      severity: 'low',
      recommendation: 'Consider adding priorities that directly support this objective.',
    });
  }
  
  // Calculate alignment score
  const mappedPriorityCount = priorityIds.length - unmappedPriorities.length;
  const mappedObjectiveCount = objectives.filter(o => objectivesWithMappings.has(o.id)).length;
  
  let alignmentScore = 0;
  if (priorityIds.length > 0 && objectives.length > 0) {
    const priorityScore = (mappedPriorityCount / priorityIds.length) * 50;
    const objectiveScore = (mappedObjectiveCount / objectives.length) * 50;
    alignmentScore = Math.round(priorityScore + objectiveScore);
  }
  
  return {
    generatedAt: new Date().toISOString(),
    workspaceId,
    totalObjectives: objectives.length,
    mappedObjectives: mappedObjectiveCount,
    totalPriorities: priorityIds.length,
    mappedPriorities: mappedPriorityCount,
    alignmentScore,
    gaps: gaps.sort((a, b) => {
      const severityOrder = { high: 0, medium: 1, low: 2 };
      return severityOrder[a.severity] - severityOrder[b.severity];
    }),
    mappings: allMappings,
    objectives,
  };
}

// ============================================================================
// Hierarchical Objective Tree
// ============================================================================

export interface ObjectiveNode {
  objective: StrategicObjective;
  children: ObjectiveNode[];
  mappedPriorities: ObjectivePriorityMapping[];
}

export async function getObjectiveTree(
  workspaceId: string,
  strategyId?: string
): Promise<ObjectiveNode[]> {
  const objectives = await listObjectives(workspaceId, strategyId);
  const objectiveIds = objectives.map(o => o.id);
  const mappings = await listMappingsForObjectives(objectiveIds);
  
  // Build tree structure
  const nodeMap = new Map<string, ObjectiveNode>();
  
  for (const objective of objectives) {
    nodeMap.set(objective.id, {
      objective,
      children: [],
      mappedPriorities: mappings.filter(m => m.objectiveId === objective.id),
    });
  }
  
  // Link children to parents
  const roots: ObjectiveNode[] = [];
  
  for (const objective of objectives) {
    const node = nodeMap.get(objective.id)!;
    
    if (objective.parentObjectiveId && nodeMap.has(objective.parentObjectiveId)) {
      nodeMap.get(objective.parentObjectiveId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  
  return roots;
}


